"use client";

import { createCustomTokenWithUserId } from "@/app/actions/loginUserWithOTP";
import { registerUserWithOTP } from "@/app/actions/registerUserWithOTP";
import { getStoredTrackingParams } from "@/utils/utmTracker";
import { app } from "@/firebase/firebase";
import { sendCustomGaEvent } from "@/scripts/ga";
import { sentOtp, verifyOtp } from "@/store/feature/user/userApi";
import { useAppDispatch } from "@/store/hooks";
import { PHONE_REGEX } from "@/utils/Constants";
import {
  getItemWithExpiry,
  setItemWithExpiry,
} from "@/utils/SetItemWithExpiry";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { Close, WhiteSpinner } from "../common/Icons";
import { Input } from "../ui/input";
import PhoneOtp from "./PhoneOtp";

type ErrorWithResponse = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

const extractErrorMessage = (error: unknown): string | null => {
  const err = error as ErrorWithResponse;
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in err &&
    typeof err.response === "object" &&
    err.response?.data?.message
  ) {
    const msg = err.response.data.message as string;
    if (typeof msg === "string" && !msg.toLowerCase().includes("request failed with status code")) {
      return msg;
    }
  }
  if (typeof error === "object" && error !== null && "message" in (error as Error)) {
    const msg = (error as Error).message;
    if (typeof msg === "string" && !msg.toLowerCase().includes("request failed with status code")) {
      return msg;
    }
  }
  return null;
};

const PhoneNumberAuth = ({
  setShow,
  compact = false,
}: {
  setShow?: React.Dispatch<React.SetStateAction<boolean>>;
  compact?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const currentPath = usePathname();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [userDetails, setUserDetails] = useState({ phone: "", fullName: "" });
  const [isPending, setIsPending] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const endTimeRef = useRef<number | null>(null);

  const isPhoneReady = userDetails.phone.trim().length >= 13;

  const getRemainingTime = () => {
    const storedEndTime = localStorage.getItem("otpTimerEnd");
    if (!storedEndTime) return 0;
    const remaining = Math.floor((+storedEndTime - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  };

  const clearOtpTimer = () => {
    localStorage.removeItem("otpTimerEnd");
    endTimeRef.current = null;
  };

  const startCountdown = (duration = 60) => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (duration <= 0) {
      clearOtpTimer();
      setResendCountdown(0);
      return;
    }

    const endTime = Date.now() + duration * 1000;
    localStorage.setItem("otpTimerEnd", endTime.toString());
    endTimeRef.current = endTime;
    setResendCountdown(duration);

    let lastRemaining = duration;

    timerRef.current = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((endTimeRef.current! - Date.now()) / 1000),
      );

      if (remaining !== lastRemaining) {
        setResendCountdown(remaining);
        lastRemaining = remaining;
      }

      if (remaining <= 0) {
        clearInterval(timerRef.current!);
        timerRef.current = null;
        clearOtpTimer();
      }
    }, 250);
  };

  useEffect(() => {
    const remaining = getRemainingTime();
    if (remaining > 0) startCountdown(remaining);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof typeof userDetails,
  ) => {
    setError(null);
    setUserDetails({ ...userDetails, [field]: e.target.value });
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone || phone.trim() === "") {
      setPhoneError("Phone number is required");
      return false;
    }
    if (
      process.env.NEXT_PUBLIC_ENVIRONMENT === "production" &&
      !PHONE_REGEX.test(phone)
    ) {
      setPhoneError("Invalid phone number format");
      return false;
    }
    setPhoneError(null);
    return true;
  };

  const sendOtpHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPending(true);
    setError(null);
    setPhoneError(null);

    const phone = userDetails.phone.trim();
    const existingPhone = getItemWithExpiry("phoneNumber");

    if (existingPhone && existingPhone !== phone) {
      startCountdown(0);
    }

    if (phone.length < 13) {
      setPhoneError("Invalid phone number format");
      setIsPending(false);
      return;
    }

    if (!validatePhoneNumber(phone)) {
      setIsPending(false);
      return;
    }

    if (phone) setItemWithExpiry("phoneNumber", phone, 2);

    const remaining = getRemainingTime();
    if (remaining > 0) {
      setResendCountdown(remaining);
      setShowOtp(true);
      setIsPending(false);
      return;
    }

    try {
      void sendCustomGaEvent("auth_otp_send_attempt", {
        path: currentPath,
        compact,
      });
      const sendOtpResponse = await dispatch(
        sentOtp({ phoneNumber: phone }),
      ).unwrap();
      void sendCustomGaEvent("auth_otp_send_success", {
        path: currentPath,
        compact,
      });

      if (sendOtpResponse.user_id) {
        localStorage.setItem("userId", sendOtpResponse.user_id);
        setUserAlreadyExists(true);
      } else {
        localStorage.removeItem("userId");
        setUserAlreadyExists(false);
      }

      setShowOtp(true);
      startCountdown();
    } catch (error) {
      console.error("Error sending OTP:", error);
      void sendCustomGaEvent("auth_otp_send_failed", {
        path: currentPath,
        compact,
      });
      setPhoneError(
        extractErrorMessage(error) ?? "Something went wrong while sending OTP.",
      );
    } finally {
      setIsPending(false);
    }
  };

  const resendOtpHandler = async () => {
    const remaining = getRemainingTime();
    if (remaining > 0 || resendLoading) {
      setResendCountdown(remaining);
      return;
    }

    setResendLoading(true);
    setOtpError(null);

    try {
      void sendCustomGaEvent("auth_otp_resend_attempt", {
        path: currentPath,
        compact,
      });
      const sendOtpResponse = await dispatch(
        sentOtp({ phoneNumber: userDetails.phone }),
      ).unwrap();
      void sendCustomGaEvent("auth_otp_resend_success", {
        path: currentPath,
        compact,
      });

      if (sendOtpResponse.user_id) {
        localStorage.setItem("userId", sendOtpResponse.user_id);
        setUserAlreadyExists(true);
      } else {
        localStorage.removeItem("userId");
        setUserAlreadyExists(false);
      }

      setOtp("");
      startCountdown();
    } catch (error) {
      console.error("Error resending OTP:", error);
      void sendCustomGaEvent("auth_otp_resend_failed", {
        path: currentPath,
        compact,
      });
      setOtpError(
        extractErrorMessage(error) ?? "Failed to resend OTP. Please try again.",
      );
    } finally {
      setResendLoading(false);
    }
  };

  const closeModal = () => {
    setShowOtp(false);
    setOtpError(null);
    setOtp("");
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const finishAuth = (userId?: string | null) => {
    localStorage.removeItem("userId");
    if (userId) localStorage.setItem("userDetails", JSON.stringify(userId));
    setShowOtp(false);
    setShow?.(false);
    document.body.classList.remove("!overflow-hidden");
    if (currentPath === "/auth") router.push("/");
  };

  const verifyOtpHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setOtpError(null);
    setIsLoggingIn(true);

    try {
      void sendCustomGaEvent("auth_otp_verify_attempt", {
        path: currentPath,
        compact,
      });
      const response = await dispatch(
        verifyOtp({ phoneNumber: String(userDetails.phone), otp }),
      ).unwrap();

      if (response.success) {
        localStorage.setItem(
          "otpVerified",
          JSON.stringify({ message: "success", time: new Date() }),
        );
      }

      const userId = localStorage.getItem("userId");

      if (userAlreadyExists || userId) {
        const tokenResponse = await createCustomTokenWithUserId({
          uid: userId || "",
        });

        if (tokenResponse.error) throw new Error(tokenResponse.error);

        if (tokenResponse.success && tokenResponse.customToken) {
          const auth = getAuth(app);
          await signInWithCustomToken(auth, tokenResponse.customToken);
          void sendCustomGaEvent("auth_otp_verify_success", {
            path: currentPath,
            compact,
            user_exists: true,
          });
          finishAuth(userId);
        } else {
          throw new Error("An unexpected error occurred during login.");
        }
        return;
      }

      setShowOtp(false);
      setOtpVerified(true);
      void sendCustomGaEvent("auth_otp_verify_success", {
        path: currentPath,
        compact,
        user_exists: false,
      });
    } catch (error) {
      console.error("OTP verification error:", error);
      void sendCustomGaEvent("auth_otp_verify_failed", {
        path: currentPath,
        compact,
      });
      setOtpError(
        extractErrorMessage(error) ?? "Invalid OTP. Please try again."
      );
      setShowOtp(true);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const registerHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPending(true);
    setError(null);

    if (!otpVerified || userAlreadyExists) {
      setIsPending(false);
      return;
    }

    const fullName = userDetails.fullName.trim();
    if (!fullName) {
      setError("Full name is required.");
      setIsPending(false);
      return;
    }

    const nameParts = fullName.split(/\s+/);
    const firstName = nameParts[0];
    const lastName =
      nameParts.length >= 2 ? nameParts[nameParts.length - 1] : null;
    const middleName = nameParts.length > 2 ? nameParts[1] : null;

    try {
      const trackingParams = getStoredTrackingParams();
      const response = await registerUserWithOTP({
        firstName,
        middleName,
        lastName,
        phone: userDetails.phone,
        ...trackingParams,
        source: trackingParams.utm_campaign || trackingParams.source || null,
      });

      if (response.error) throw new Error(response.error);

      if (response.success && response.customToken) {
        const auth = getAuth(app);
        await signInWithCustomToken(auth, response.customToken);
        finishAuth(response.userDetails?.userId ?? null);
      } else {
        throw new Error("An unexpected error occurred during registration.");
      }
    } catch (err: any) {
      console.error("SignUp failed:", err);
      setError(`Failed to sign up: ${err.message}`);
    } finally {
      setIsPending(false);
    }
  };

  const displayTime = useMemo(
    () => resendCountdown.toString().padStart(2, "0"),
    [resendCountdown],
  );

  const getResendButtonText = () => {
    if (resendLoading) return "Resending...";
    if (resendCountdown > 0) return `Sending a new code in (${displayTime}s)`;
    return "Send it again.";
  };

  return (
    <>
      {!otpVerified ? (
        <form onSubmit={sendOtpHandler} className="flex flex-col mt-5">
          <PhoneInput
            className="w-full border-black border-[1.5px] rounded-[8px] mt-4 ff_poppins"
            forceDialCode
            defaultCountry="in"
            hideDropdown={true}
            inputClassName={`${compact ? "!text-[16px] !h-[44px]" : "!text-[20px] !h-[50px]"} ff_poppins`}
            countrySelectorStyleProps={{
              buttonClassName: `${compact ? "!h-[44px] !text-[16px]" : "!h-[50px] !text-[20px]"} !bg-white !border-none !rounded-none !px-3 ff_poppins`,
            }}
            value={userDetails.phone}
            onChange={(value) => {
              setPhoneError(null);
              setError(null);
              setUserDetails({ ...userDetails, phone: value });
            }}
            inputProps={{
              onPaste: (e) => e.preventDefault(),
              onCopy: (e) => e.preventDefault(),
              onCut: (e) => e.preventDefault(),
            }}
          />
          <div className="flex gap-2 rounded-lg py-2 mt-2 text-sm text-gray-600">
            <Image
              src="/assets/images/svg/whatapp.svg"
              alt="WhatsApp"
              width={18}
              height={18}
              className="transition-transform duration-300 hover:scale-110"
            />
            <p className="ff_poppins">
              Use your{" "}
              <span className="font-medium text-green-600">
                WhatsApp number
              </span>{" "}
              for faster updates.
            </p>
          </div>
          {phoneError && (
            <p className="text-red-500 mt-2 ff_poppins text-sm">{phoneError}</p>
          )}
          <button
            type="submit"
            disabled={isPending || !isPhoneReady}
            className={`font-semibold ff_poppins w-full mt-5 text-white rounded-xl gradient_button_shadow bg-button-gradient text-center hover:scale-105 duration-300 ease-in-out ${
              compact
                ? "text-base py-3"
                : "text-lg md:text-xl xl:text-2xl py-[14px] sm:py-4 lg:py-[19.5px]"
            } ${
              isPending || !isPhoneReady
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            }`}
          >
            {isPending ? <WhiteSpinner /> : "Get Started"}
          </button>
        </form>
      ) : (
        <form
          className="flex items-center flex-col mt-5"
          onSubmit={registerHandler}
        >
          <Input
            value={userDetails.fullName}
            required
            onChange={(e) => handleInputChange(e, "fullName")}
            className={`border-[1.5px] font-medium border-black ff_poppins placeholder:font-normal ${
              compact
                ? "text-sm h-[42px] placeholder:text-sm"
                : "text-sm sm:text-base md:text-lg 3xl:text-xl! h-[40px] sm:h-[43px] xl:h-[47px] 3xl:h-[50px] placeholder:text-sm sm:placeholder:text-base md:placeholder:text-lg 3xl:placeholder:text-xl"
            }`}
            placeholder="Full Name"
          />
          {error && (
            <p className="text-red-500 mt-2 ff_poppins text-sm">{error}</p>
          )}
          <button
            type="submit"
            disabled={isPending}
            className={`font-semibold ff_poppins w-full mt-5 text-white rounded-xl gradient_button_shadow bg-button-gradient text-center hover:scale-105 duration-300 ease-in-out ${
              compact
                ? "text-base py-3"
                : "text-lg md:text-xl xl:text-2xl py-[14px] sm:py-4 lg:py-[19.5px]"
            } ${
              isPending ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            {isPending ? <WhiteSpinner /> : "Register"}
          </button>
        </form>
      )}
      {showOtp && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
          <div className="bg-white p-5 2xl:p-10 rounded-2xl mx-3 relative max-w-2xl w-full">
            <span
              onClick={closeModal}
              className="absolute top-3 right-3 cursor-pointer size-4 sm:size-5 lg:size-6 xl:size-5 2xl:size-6"
            >
              <Close />
            </span>
            <PhoneOtp
              isPending={isLoggingIn}
              error={otpError}
              setShowOtp={setShowOtp}
              resendOtpHandler={resendOtpHandler}
              resendLoading={resendLoading}
              resendCountdown={resendCountdown}
              getResendButtonText={getResendButtonText}
              setOtp={setOtp}
              otp={otp}
              submitHandler={verifyOtpHandler}
              phoneNumber={userDetails.phone}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PhoneNumberAuth;
